import React, { useState, useCallback } from 'react';
import { X, Mail, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { authApi } from '../../services/api';

const ANIMATION_DURATION = 150;

function ForgotPasswordModal({ isOpen, onClose }) {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email.trim()) {
      setError('L\'adresse email est requise');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Veuillez entrer une adresse email valide');
      return;
    }

    setIsSubmitting(true);

    try {
      await authApi.forgotPassword(email);
      setSuccess(true);
    } catch (error) {
      setError(error.message || 'Erreur lors de l\'envoi de l\'email');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = useCallback(() => {
    if (isClosing) return;
    setIsClosing(true);
    setTimeout(() => {
      setEmail('');
      setError('');
      setSuccess(false);
      setIsClosing(false);
      onClose();
    }, ANIMATION_DURATION);
  }, [isClosing, onClose]);

  if (!isOpen) return null;

  return (
    <div className={`fixed inset-0 overlay-frost flex items-center justify-center p-4 z-50 ${isClosing ? 'animate-fade-out' : 'animate-fade-in'}`}>
      <div className={`glass-deep border border-white/10 rounded-lg shadow-xl max-w-md w-full ${isClosing ? 'animate-modal-out' : 'animate-modal-in'}`}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div className="flex items-center space-x-3">
            <div className="bg-yellow-500/20 p-2 rounded-lg">
              <Mail className="h-6 w-6 text-yellow-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">
                Mot de passe oublié
              </h2>
              <p className="text-sm text-gray-400">
                Réinitialisez votre mot de passe
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-200 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {success ? (
            <div className="text-center py-4">
              <div className="bg-green-500/20 p-3 rounded-full w-fit mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-green-400" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">
                Email envoyé !
              </h3>
              <p className="text-gray-400 mb-6">
                Si un compte existe avec cette adresse, vous recevrez
                un email avec les instructions de réinitialisation.
              </p>
              <button
                onClick={handleClose}
                className="px-6 py-2 bg-yellow-500 text-black rounded-lg
                           hover:bg-yellow-400 transition-colors font-medium"
              >
                Fermer
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-md p-4 flex items-center space-x-2">
                  <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0" />
                  <p className="text-red-400 text-sm">{error}</p>
                </div>
              )}

              <p className="text-gray-300 text-sm">
                Entrez votre adresse email et nous vous enverrons un lien
                pour réinitialiser votre mot de passe.
              </p>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                  Adresse email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="votre@email.fr"
                    className="w-full pl-10 pr-4 py-3 bg-black/30 border border-white/10
                               text-white placeholder-gray-500 rounded-lg
                               focus:outline-none focus:ring-2 focus:ring-yellow-300
                               focus:border-transparent transition-all"
                    disabled={isSubmitting}
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t border-white/10">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-2 text-gray-300 bg-white/15 border border-white/15
                             rounded-md hover:bg-white/20 transition-colors"
                  disabled={isSubmitting}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || !email.trim()}
                  className="px-4 py-2 bg-yellow-500 text-black rounded-md
                             hover:bg-yellow-400 disabled:bg-white/10
                             disabled:text-gray-400 disabled:cursor-not-allowed
                             transition-colors flex items-center space-x-2"
                >
                  <Send className="h-4 w-4" />
                  <span>{isSubmitting ? 'Envoi...' : 'Envoyer'}</span>
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default ForgotPasswordModal;
